import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import titleize from '@vangst/lib/titleize'
import useCompany, {
  CompanyComputed,
  optimisticDeleteCompany,
  prefetchCompany,
} from '@vangst/services/oogst/company/useCompany'
import { prefetchCompanyJobPostings } from '@vangst/services/oogst/company/useCompanyJobPostings'
import { prefetchCompanyPhotos } from '@vangst/services/oogst/company/useCompanyPhotos'
import { fetchPage } from '@vangst/services/oogst/page'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import React from 'react'
import Busy from '../../../components/feedback/Busy'
import Breadcrumbs from '../../../components/navigation/Breadcrumbs'
import CompanyBlockList from '../../../domains/company/CompanyBlockList'
import CompanyFollowersGrid from '../../../domains/company/CompanyFollowersGrid'
import CompanyMeta from '../../../domains/company/CompanyMeta'
import CompanyRenderer from '../../../domains/company/CompanyRenderer'
import FollowEditSignup from '../../../domains/company/FollowEditSignup'
import PostAJob from '../../../domains/company/PostAJob'
import CustomError from '../../_error'

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

type StaticProps = GetStaticPropsContext<{ displayname?: string }>

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const displayname = params?.displayname ? params.displayname : undefined
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[displayname]', displayname || '')

  const vars = displayname
    ? {
        atsFree: {
          displayname,
          first: 24,
        },
        company: { displayname },
        followers: { displayname, first: 24 },
      }
    : undefined

  let company
  if (vars) {
    company = await prefetchCompany(queryClient, vars.company)
    await prefetchCompanyPhotos(queryClient, vars.company)
    await prefetchCompanyJobPostings(queryClient, vars.atsFree)
  }
  const co = company?.getClient as CompanyComputed
  const is403 = company != null && company?.is403
  if (is403 && vars) optimisticDeleteCompany(queryClient, vars?.company)

  const page = await fetchPage(queryClient, { rawUrl })

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    {
      href: Routes.COMPANIES,
      text: 'Companies',
    },
    {
      href: null,
      text: titleize(displayname || 'Company'),
    },
  ]

  return {
    props: {
      crumbs,
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      displayname,
      is403,
      isPending: company?.getClient?.permissions?.readPendingBanner ?? true,
      jobsRoute: co?.routes?.jobsIndex ?? null,
      page: page?.getPage ? page.getPage : null,
      vars,
    },
    notFound: company?.getClient == null && is403 === false,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

function BusyBusy() {
  return <Busy className="mt-12 flex items-center justify-center" />
}

const ThePage = React.memo(function ThePage(props: PropsType) {
  const { crumbs, displayname, page, vars } = props
  if (!displayname || !vars) return <BusyBusy />

  return (
    <>
      <CompanyMeta page={page} crumbs={crumbs} variables={vars.company} />
      <Breadcrumbs crumbs={crumbs} />
      <div className="contain-c template-aside-content mood-white mt-8">
        <CompanyRenderer as="detail" variables={vars.company} />

        <aside className="contain-xy">
          <header className="mt-6 min-h-20 pb-2 sm:pb-2 md:flex md:items-center">
            <CompanyRenderer as="header" variables={vars.company} />
          </header>
          <section className="my-8">
            <h2 className="text-lg">Company Information:</h2>
            <CompanyBlockList at="sidebar" variables={vars.company} />
          </section>
          <div className="grid content-start">
            <FollowEditSignup
              as="btn"
              btnTxt="Follow"
              restricted
              variables={vars.company}
            />
            <PostAJob className="ml-2 mt-4" slug={displayname} />
          </div>
          <hr className="mt-8" />
          <section>
            <CompanyFollowersGrid
              as="avatar"
              at="sidebar"
              classNameList="grid-avatars h-40"
              take={15}
              variables={vars.followers}
            />
          </section>
        </aside>
      </div>
    </>
  )
})

function CompanyMight403(props: PropsType) {
  const { authenticated, authenticating, viewer } = useViewer()
  const vars = props.vars?.company ? props.vars.company : { displayname: '' }
  const { data, isLoading } = useCompany(vars, {
    enabled: !!(viewer != null && props.vars?.company),
  })
  const unauthorized =
    !authenticated || (isLoading === false && data?.getClient == null)

  if (authenticating || isLoading) return <BusyBusy />
  if (unauthorized) return <CustomError statusCode={403} />
  return <ThePage {...props} />
}

export default function CompaniesDetailPage(props: PropsType) {
  if (!props.displayname || !props.vars) return <BusyBusy />
  if (props.is403 === true) return <CompanyMight403 {...props} />
  return <ThePage {...props} />
}
